import React, { useState, useEffect } from 'react';
import { 
  FiUpload, 
  FiCheck, 
  FiAlertTriangle, 
  FiDownload, 
  FiRefreshCw,
  FiExternalLink,
  FiSettings
} from 'react-icons/fi';
import api from '../services/api';

export default function GoogleSheetsImport() {
  const [step, setStep] = useState(1); // 1: URL Input, 2: Preview, 3: Mapping, 4: Import
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [sheetUrl, setSheetUrl] = useState('');
  const [range, setRange] = useState('A:Z');
  
  // Preview data
  const [previewData, setPreviewData] = useState(null);
  const [validation, setValidation] = useState(null);
  
  // Mapping state
  const [categoryMappings, setCategoryMappings] = useState({});
  const [stateMappings, setStateMappings] = useState({});
  const [availableMappings, setAvailableMappings] = useState({ states: [], categories: [] });
  
  // Import state
  const [importResult, setImportResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  
  // History state
  const [importHistory, setImportHistory] = useState([]);

  useEffect(() => {
    fetchAvailableMappings();
    fetchImportHistory();
  }, []);

  const fetchAvailableMappings = async () => {
    try {
      const response = await api.get('/admin/import/mappings');
      setAvailableMappings(response.data);
      
      // Auto-populate state mappings
      const autoStateMappings = {};
      response.data.states.forEach(state => {
        autoStateMappings[state.code] = state.id;
        autoStateMappings[state.name] = state.id;
      });
      setStateMappings(autoStateMappings);
      
    } catch (error) {
      console.error('Failed to fetch mappings:', error);
    }
  };

  const fetchImportHistory = async () => {
    try {
      const response = await api.get('/admin/import/history');
      setImportHistory(response.data.imports || []);
    } catch (error) {
      console.error('Failed to fetch import history:', error);
    }
  };

  const handlePreview = async () => {
    if (!sheetUrl.trim()) {
      setError('Please enter a Google Sheets URL');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/admin/import/sheets/preview', {
        spreadsheetUrl: sheetUrl,
        range
      });
      
      setPreviewData(response.data.preview);
      setValidation(response.data.validation);
      setStep(2);
      
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to preview sheet');
    } finally {
      setLoading(false);
    }
  };

  const handleStartImport = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/admin/import/sheets/start', {
        spreadsheetUrl: sheetUrl,
        range,
        categoryMappings,
        stateMappings
      });
      
      setJobId(response.data.jobId);
      setImportResult(response.data);
      setStep(4);
      
      // Start polling for job status if we have a job ID
      if (response.data.jobId) {
        pollJobStatus(response.data.jobId);
      }
      
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to start import');
    } finally {
      setLoading(false);
    }
  };

  const pollJobStatus = async (jobId) => {
    try {
      const response = await api.get(`/admin/import/job/${jobId}`);
      setImportProgress(response.data.job);
      
      // Continue polling if job is not finished
      if (!response.data.job.finishedOn && !response.data.job.failedReason) {
        setTimeout(() => pollJobStatus(jobId), 2000);
      } else {
        // Job finished, refresh history
        fetchImportHistory();
      }
    } catch (error) {
      console.error('Failed to get job status:', error);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSheetUrl('');
    setRange('A:Z');
    setPreviewData(null);
    setValidation(null);
    setImportResult(null);
    setJobId(null);
    setImportProgress(null);
    setError('');
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          Google Sheets Import
        </h2>
        <p style={{ color: '#6B7280' }}>
          Import questions from Google Sheets with automatic validation and state mapping.
        </p>
      </div>

      {/* Progress Indicator */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '2rem',
        padding: '1rem',
        backgroundColor: '#F9FAFB',
        borderRadius: '8px'
      }}>
        {[1, 2, 3, 4].map((stepNum) => (
          <div key={stepNum} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '50%',
              backgroundColor: step >= stepNum ? '#3B82F6' : '#E5E7EB',
              color: step >= stepNum ? 'white' : '#6B7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              {stepNum}
            </div>
            {stepNum < 4 && (
              <div style={{
                width: '3rem',
                height: '2px',
                backgroundColor: step > stepNum ? '#3B82F6' : '#E5E7EB',
                margin: '0 0.5rem'
              }} />
            )}
          </div>
        ))}
        <div style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
          {step === 1 && 'Enter Sheet URL'}
          {step === 2 && 'Preview Data'}
          {step === 3 && 'Configure Mapping'}
          {step === 4 && 'Import Progress'}
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          color: '#DC2626',
          padding: '0.75rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <FiAlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Step 1: URL Input */}
      {step === 1 && (
        <div style={{ 
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
            Google Sheets Configuration
          </h3>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '0.875rem', 
              fontWeight: '500', 
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Google Sheets URL *
            </label>
            <input
              type="url"
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/your-sheet-id/edit"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
            <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
              Make sure the sheet is shared with: pa-quiz-app-import-service@pa-quiz-app.iam.gserviceaccount.com
            </p>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '0.875rem', 
              fontWeight: '500', 
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Range (Optional)
            </label>
            <input
              type="text"
              value={range}
              onChange={(e) => setRange(e.target.value)}
              placeholder="A:Z"
              style={{
                width: '200px',
                padding: '0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <button
            onClick={handlePreview}
            disabled={loading}
            style={{
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? <FiRefreshCw className="animate-spin" size={16} /> : <FiExternalLink size={16} />}
            {loading ? 'Loading Preview...' : 'Preview Sheet'}
          </button>
        </div>
      )}

      {/* Step 2: Preview Data */}
      {step === 2 && previewData && (
        <div style={{ 
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '1.5rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600' }}>
              Sheet Preview ({previewData.previewRows} of {previewData.totalRows} rows)
            </h3>
            <button
              onClick={() => setStep(3)}
              style={{
                backgroundColor: '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Continue to Mapping
            </button>
          </div>

          {/* Validation Results */}
          {validation && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ 
                display: 'flex', 
                gap: '1rem',
                padding: '0.75rem',
                backgroundColor: validation.isValid ? '#F0FDF4' : '#FEF2F2',
                border: `1px solid ${validation.isValid ? '#BBF7D0' : '#FECACA'}`,
                borderRadius: '6px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {validation.isValid ? (
                    <FiCheck size={16} style={{ color: '#10B981' }} />
                  ) : (
                    <FiAlertTriangle size={16} style={{ color: '#DC2626' }} />
                  )}
                  <span style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '500',
                    color: validation.isValid ? '#10B981' : '#DC2626'
                  }}>
                    {validation.validRows} valid rows, {validation.errors.length} errors
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Preview Table */}
          <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  {previewData.headers.map((header, idx) => (
                    <th key={idx} style={{
                      padding: '0.5rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      border: '1px solid #E5E7EB'
                    }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.data.slice(0, 5).map((row, idx) => (
                  <tr key={idx}>
                    {previewData.headers.map((header, colIdx) => (
                      <td key={colIdx} style={{
                        padding: '0.5rem',
                        border: '1px solid #E5E7EB',
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {row[header] || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setStep(1)}
              style={{
                backgroundColor: '#F3F4F6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Mapping Configuration */}
      {step === 3 && (
        <div style={{ 
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '1.5rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600' }}>
              Configure Mappings
            </h3>
            <button
              onClick={handleStartImport}
              disabled={loading}
              style={{
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? <FiRefreshCw className="animate-spin" size={16} /> : <FiUpload size={16} />}
              {loading ? 'Starting Import...' : 'Start Import'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            {/* Category Mappings */}
            <div>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                Category Mappings
              </h4>
              <div style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem' }}>
                Map sheet categories to database categories
              </div>
              {availableMappings.categories.map((category) => (
                <div key={category.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  <input
                    type="text"
                    placeholder="Sheet category name"
                    onChange={(e) => setCategoryMappings({
                      ...categoryMappings,
                      [e.target.value]: category.id
                    })}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontSize: '0.75rem'
                    }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>→</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>{category.name}</span>
                </div>
              ))}
            </div>

            {/* State Mappings - Auto-populated */}
            <div>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                State Mappings (Auto-configured)
              </h4>
              <div style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem' }}>
                State codes and names are automatically mapped
              </div>
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#F9FAFB',
                borderRadius: '6px',
                fontSize: '0.75rem'
              }}>
                <strong>Available:</strong> CA, NY, PA, TX, FL, etc.<br/>
                <strong>Example:</strong> "CA" → California, "NY" → New York
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
            <button
              onClick={() => setStep(2)}
              style={{
                backgroundColor: '#F3F4F6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Import Progress/Results */}
      {step === 4 && (
        <div style={{ 
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
            Import Results
          </h3>

          {importResult && (
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                padding: '1rem',
                backgroundColor: '#F0FDF4',
                border: '1px solid #BBF7D0',
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <FiCheck size={16} style={{ color: '#10B981' }} />
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10B981' }}>
                    Import Started Successfully
                  </span>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                  Processing {importResult.totalRows} rows
                  {importResult.estimatedTime && ` (Est. ${importResult.estimatedTime}s)`}
                </div>
              </div>

              {importProgress && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', color: '#374151' }}>Progress</span>
                    <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                      {importProgress.progress || 0}%
                    </span>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '8px', 
                    backgroundColor: '#E5E7EB', 
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${importProgress.progress || 0}%`,
                      height: '100%',
                      backgroundColor: '#3B82F6',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            onClick={resetForm}
            style={{
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Import Another Sheet
          </button>
        </div>
      )}

      {/* Import History */}
      {importHistory.length > 0 && (
        <div style={{ 
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '1.5rem',
          marginTop: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
            Recent Imports
          </h3>
          
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Admin</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Rows</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.slice(0, 5).map((item, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                    <td style={{ padding: '0.75rem' }}>
                      {new Date(item.timestamp).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {item.first_name} {item.last_name}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {JSON.parse(item.detail || '{}').totalRows || 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        backgroundColor: '#10B981',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem'
                      }}>
                        Completed
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}